import { Button } from "@/components/ui";
import { Textarea } from "@/components/ui/textarea";
import PropTypes from "prop-types";
import { memo } from "react";
import { Markdown } from "../markdown";
import { formatCitationsMarkdown } from "../utils/citationFormatter";

/**
 * MessageContent component - Wrapper for Markdown rendering with className management
 * Handles both user and assistant message content display, including edit mode
 *
 * @component
 * @param {Object} props
 * @param {string} props.displayContent - Content to display
 * @param {boolean} props.effectiveIsStreaming - Whether message is streaming
 * @param {function} props.handleCitationClick - Handler for citation clicks
 * @param {boolean} props.isUser - Whether this is a user message
 * @param {boolean} props.isEditing - Whether in edit mode
 * @param {string} props.editContent - Content being edited
 * @param {Object} props.editTextareaRef - Ref for edit textarea
 * @param {function} props.handleInputChange - Handler for edit input changes
 * @param {function} props.handleEditSubmit - Handler for submitting edits
 * @param {function} props.handleCancelEdit - Handler for canceling edits
 * @param {Object} props.sources - Citation sources data
 * @param {Object} props.message - The message object
 */
const PureMessageContent = ({
  displayContent,
  effectiveIsStreaming = false,
  handleCitationClick = null,
  isUser,
  isEditing = false,
  editContent = "",
  editTextareaRef = null,
  handleInputChange = null,
  handleEditSubmit = null,
  handleCancelEdit = null,
  sources = null,
  message = null,
}) => {
  if (isUser) {
    if (isEditing) {
      return (
        <div
          className="flex flex-col w-full bg-transparent"
          data-edit-mode="true"
        >
          <div className="bg-gray-100 dark:bg-[#212121] rounded-2xl p-3 shadow-md relative">
            <Textarea
              ref={editTextareaRef}
              value={editContent}
              onChange={handleInputChange}
              className="min-h-[80px] w-full resize-none border-0 bg-transparent p-0 pr-32 text-md text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 focus-visible:ring-0 focus-visible:ring-offset-0"
              placeholder="Edit your message..."
              autoFocus={true}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleEditSubmit();
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  handleCancelEdit();
                }
                e.stopPropagation();
              }}
              style={{
                overflow: "hidden",
                resize: "none",
                height: "auto",
              }}
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-3 right-3 flex gap-2">
              <Button
                variant="outline"
                onClick={handleCancelEdit}
                className="px-4 py-2 text-sm rounded-full bg-gray-200 dark:bg-zinc-800 border-0 text-gray-700 dark:text-white hover:bg-gray-300 dark:hover:bg-zinc-700 transition-colors"
              >
                Cancel
              </Button>
              <Button
                onClick={handleEditSubmit}
                className="px-4 py-2 text-sm rounded-full bg-gray-800 dark:bg-zinc-300 text-white dark:text-black hover:bg-gray-700 dark:hover:bg-zinc-200 transition-colors"
              >
                Send
              </Button>
            </div>
          </div>
        </div>
      );
    }
  }

  // For assistant messages, render content without avatar (avatar will be positioned outside)
  if (!isUser) {
    // Format sources as markdown for display
    const markdownCitations = sources ? formatCitationsMarkdown(sources) : null;

    // Only show Sources section for Perplexity models (sonar/sonar-pro)
    // Check provider in metadata or model name to identify Perplexity models
    const modelName = message?.model || message?.metadata?.model || "";
    const isPerplexityModel =
      message?.metadata?.provider === "perplexity" ||
      modelName.includes("sonar") ||
      modelName.includes("r1-1776");

    return (
      <>
        <Markdown
          html
          isStreaming={effectiveIsStreaming}
          darkMode={true}
          className="message-content"
          onCitationClick={handleCitationClick}
          sources={sources}
          isUser={isUser}
        >
          {typeof displayContent === "string"
            ? displayContent
            : String(displayContent)}
        </Markdown>
        {markdownCitations && !effectiveIsStreaming && isPerplexityModel && (
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Sources:
            </h3>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <Markdown
                html={false}
                darkMode={true}
                className="sources-content"
                sources={null}
                isUser={false}
              >
                {markdownCitations}
              </Markdown>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <Markdown
      html
      isStreaming={effectiveIsStreaming}
      darkMode={true}
      className="message-content"
      onCitationClick={handleCitationClick}
      sources={sources}
      isUser={isUser}
    >
      {typeof displayContent === "string"
        ? displayContent
        : String(displayContent)}
    </Markdown>
  );
};

PureMessageContent.propTypes = {
  displayContent: PropTypes.string.isRequired,
  effectiveIsStreaming: PropTypes.bool,
  handleCitationClick: PropTypes.func,
  isUser: PropTypes.bool.isRequired,
  isEditing: PropTypes.bool,
  editContent: PropTypes.string,
  editTextareaRef: PropTypes.object,
  handleInputChange: PropTypes.func,
  handleEditSubmit: PropTypes.func,
  handleCancelEdit: PropTypes.func,
  sources: PropTypes.object,
  message: PropTypes.object,
};

export const MessageContent = memo(
  PureMessageContent,
  (prevProps, nextProps) => {
    if (prevProps.isEditing !== nextProps.isEditing) {
      return false;
    }
    if (nextProps.isEditing) return false;
    if (prevProps.effectiveIsStreaming !== nextProps.effectiveIsStreaming) {
      return false;
    }
    if (
      !nextProps.isEditing &&
      prevProps.displayContent !== nextProps.displayContent
    ) {
      return false;
    }
    if (prevProps.sources !== nextProps.sources) {
      return false;
    }
    return true;
  }
);

export default MessageContent;
